/* Vendored from the board kit on feat/board-kit-legend-scroll (b834d95, verifier-CONFIRMED, not yet on master).
 * Manifest Review needs the docked record and the top scroll rail now; when that branch lands, import this
 * from components/board instead and delete this copy. Kept byte-close to the kit on purpose. */
/**
 * Horizontal scroll bars on the TOP and the bottom of a wide table.
 *
 *   useSyncedScroll  the hook: measures a scroll frame and keeps a top rail in
 *                    step with it, both ways. Reusable by any table.
 *   DualScroll       the component: the rail, then the frame (a labelled,
 *                    focusable region whose own scroll bar is the bottom one).
 *
 * The rail is an empty strip whose only content is a track as wide as the
 * frame can scroll, so its native scroll bar has the same length, range and
 * behaviour (drag, click the track, wheel) as the frame's. It stops short of
 * the frame's vertical scroll bar by the same gutter, so both tracks line up.
 *
 * No feedback loop, no fight with an animation, and no lasting split:
 *   - Each side remembers the position it was last seen at or SET to. A scroll
 *     event that finds a side where it was left is our own write's echo (even
 *     a frame late) and is swallowed; anything else is that side moving on
 *     its own.
 *   - The first side to move on its own takes the LOCK (it is the source of
 *     truth) and carries the other along. Nothing ever writes into the locked
 *     side, so a smooth scroll (arrow keys, scrollBy, an animated wheel step,
 *     a click on the track) runs to its end on the side that started it.
 *   - If the other side also moves on its own while the lock is held (a
 *     script, a second input), neither is written until scrolling settles:
 *     writing either could abort an animation.
 *   - The lock is released when every side that moved has fired `scrollend`,
 *     or SETTLE_MS after the last move where `scrollend` never comes. If the
 *     locked side ends first while the other is still moving, the lock passes
 *     to that side and the ended one catches up at once.
 *   - On release a reconcile pass writes the side that did NOT move last to
 *     the position of the one that did (a side whose move is still on its
 *     way, found away from where it was left, counts as the later one), and
 *     one more pass runs on the next animation frame. The bars therefore
 *     always converge within a frame of scrolling settling (verifier P2,
 *     2026-10-05: rail 300 then frame 600 within 30 ms stayed split for good).
 * Shown only while the frame overflows; measured before paint on every
 * render, and again on resize (ResizeObserver on the frame and its table,
 * with a window resize fallback) and once the web fonts have loaded.
 *
 * The rail is aria-hidden and out of the Tab order: it repeats the frame's
 * own scroll bar, and the frame stays the focusable, keyboard-scrollable
 * region. Nothing here animates.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface SyncedScrollMetrics {
  /** The frame is wider inside than out (the rail is shown). */
  overflow: boolean;
  /** Width of the rail's track, so the rail scrolls exactly as far as the frame. */
  trackWidth: number;
  /** The frame's vertical scroll bar width; the rail stops short by this much. */
  gutter: number;
}

const NONE: SyncedScrollMetrics = { overflow: false, trackWidth: 0, gutter: 0 };

type Side = 'frame' | 'rail';
/** How long a side keeps the lock after its last move when no `scrollend` arrives (longer than an animation frame, shorter than a person switching bars). */
export const SETTLE_MS = 150;
const clock = () => Date.now();
const otherSide = (s: Side): Side => (s === 'frame' ? 'rail' : 'frame');
const nextFrame = (fn: () => void): (() => void) => {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    const id = window.requestAnimationFrame(fn);
    return () => window.cancelAnimationFrame(id);
  }
  const id = setTimeout(fn, 16);
  return () => clearTimeout(id);
};

export function useSyncedScroll<F extends HTMLElement = HTMLDivElement>() {
  const frameRef = useRef<F | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const [metrics, setMetrics] = useState<SyncedScrollMetrics>(NONE);
  /** Where each side was last seen, or last SET to by us: an event that finds a side there is our echo. */
  const known = useRef<Record<Side, number>>({ frame: 0, rail: 0 });
  /** The side being written right now (a synchronous scroll event from it is our own write). */
  const writing = useRef<Side | null>(null);
  /** The source of truth while scrolling: the side that moved first, whether the other side also moved on its own, and which sides have not ended. */
  const lock = useRef<{ side: Side | null; conflict: boolean; moving: Set<Side> }>({ side: null, conflict: false, moving: new Set() });
  /** The side that moved on its own most recently, and when each last did. */
  const last = useRef<Side>('frame');
  const movedAt = useRef<Record<Side, number>>({ frame: 0, rail: 0 });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelFinal = useRef<(() => void) | null>(null);
  /** The rail element `known.rail` describes (the rail mounts and unmounts with the overflow). */
  const knownRail = useRef<HTMLDivElement | null>(null);

  const el = useCallback((s: Side): HTMLElement | null => (s === 'frame' ? frameRef.current : railRef.current), []);

  /** Write one side, marking it so its scroll event (now or a frame late) is known as ours. */
  const write = useCallback((s: Side, v: number) => {
    const d = el(s);
    if (!d) return;
    known.current[s] = v;
    writing.current = s;
    try { d.scrollLeft = v; } finally { writing.current = null; }
    // What the side actually took (it may clamp or round).
    known.current[s] = d.scrollLeft;
  }, [el]);

  /** Bring the bars together: the side that moved last (or whose move is still on its way) is the truth. */
  const reconcile = useCallback((prefer?: Side) => {
    const f = frameRef.current;
    const r = railRef.current;
    if (!f || !r) return;
    let truth: Side = prefer || last.current;
    const o = otherSide(truth);
    // A side found away from where it was left has moved since, and its event has not arrived yet: it is the later move.
    if (Math.abs((el(o) as HTMLElement).scrollLeft - known.current[o]) >= 1) truth = o;
    const src = el(truth) as HTMLElement;
    const dst = el(otherSide(truth)) as HTMLElement;
    known.current[truth] = src.scrollLeft;
    if (Math.abs(dst.scrollLeft - src.scrollLeft) >= 1) write(otherSide(truth), src.scrollLeft);
    else known.current[otherSide(truth)] = dst.scrollLeft;
  }, [el, write]);

  /** Scrolling has settled: drop the lock, reconcile now, and once more on the next frame. */
  const release = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    lock.current = { side: null, conflict: false, moving: new Set() };
    reconcile();
    if (cancelFinal.current) cancelFinal.current();
    cancelFinal.current = nextFrame(() => {
      cancelFinal.current = null;
      if (lock.current.side === null) reconcile();
    });
  }, [reconcile]);

  const arm = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; release(); }, SETTLE_MS);
  }, [release]);

  /** One side fired a scroll event. */
  const follow = useCallback((from: Side) => {
    if (writing.current === from) return;
    const src = el(from);
    const dst = el(otherSide(from));
    if (!src || !dst) return;
    const at = src.scrollLeft;
    // Our own write's echo, or no real move.
    if (Math.abs(at - known.current[from]) < 1) return;
    known.current[from] = at;
    last.current = from;
    // A lock whose side has been still past SETTLE_MS (its end and its timer both missed, e.g. a throttled tab) is over.
    const held = lock.current.side;
    if (held && held !== from && clock() - movedAt.current[held] >= SETTLE_MS) release();
    movedAt.current[from] = clock();
    const L = lock.current;
    L.moving.add(from);
    arm();
    if (L.side === null) { L.side = from; L.conflict = false; }
    if (L.side !== from) { L.conflict = true; return; }
    if (L.conflict) return;
    const to = otherSide(from);
    // The other side has moved on its own (its event is on the way): write neither until scrolling settles.
    if (Math.abs(dst.scrollLeft - known.current[to]) >= 1) { L.conflict = true; return; }
    if (Math.abs(dst.scrollLeft - at) >= 1) write(to, at);
  }, [el, release, arm, write]);

  /** One side's scroll ended. */
  const ended = useCallback((s: Side) => {
    const L = lock.current;
    if (!L.moving.has(s)) return;
    L.moving.delete(s);
    if (L.moving.size === 0) { release(); return; }
    if (L.side === s) {
      // The leader is done while the other side still moves: the lock passes to it, and the ended side catches up now.
      const next = otherSide(s);
      L.side = next;
      L.conflict = false;
      reconcile(next);
    }
  }, [release, reconcile]);

  const measure = useCallback(() => {
    const f = frameRef.current;
    if (!f) return;
    const scrollWidth = f.scrollWidth;
    const clientWidth = f.clientWidth;
    const overflow = scrollWidth > clientWidth + 1;
    const gutter = overflow ? Math.max(0, f.offsetWidth - clientWidth - f.clientLeft * 2) : 0;
    const r = railRef.current;
    if (r !== knownRail.current) { knownRail.current = r; if (r) known.current.rail = r.scrollLeft; }
    // Same range on both sides: (track - rail) === (scrollWidth - clientWidth).
    const trackWidth = overflow ? (r && r.clientWidth ? scrollWidth - clientWidth + r.clientWidth : scrollWidth) : 0;
    setMetrics((m) => (m.overflow === overflow && m.trackWidth === trackWidth && m.gutter === gutter ? m : { overflow, trackWidth, gutter }));
    // A new layout puts the rail where the table is, but never while either bar is being scrolled.
    if (r && lock.current.side === null && Math.abs(r.scrollLeft - f.scrollLeft) >= 1) reconcile('frame');
  }, [reconcile]);

  // Before paint, every render: a column added or removed shows (or hides) the rail without a visible jump.
  useLayoutEffect(() => { measure(); });

  useEffect(() => {
    const f = frameRef.current;
    if (!f) return undefined;
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(f);
      if (f.firstElementChild) ro.observe(f.firstElementChild);
    }
    window.addEventListener('resize', measure);
    let alive = true;
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    if (fonts && fonts.ready && typeof fonts.ready.then === 'function') fonts.ready.then(() => { if (alive) measure(); }).catch(() => {});
    return () => {
      alive = false;
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  // `scrollend` on both sides (React 18 has no prop for it). The rail mounts with the overflow, so this re-binds then.
  useEffect(() => {
    const f = frameRef.current;
    const r = railRef.current;
    const onFrameEnd = () => ended('frame');
    const onRailEnd = () => ended('rail');
    if (f) f.addEventListener('scrollend', onFrameEnd);
    if (r) r.addEventListener('scrollend', onRailEnd);
    return () => {
      if (f) f.removeEventListener('scrollend', onFrameEnd);
      if (r) r.removeEventListener('scrollend', onRailEnd);
    };
  }, [ended, metrics.overflow]);

  // Nothing fires after unmount.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (cancelFinal.current) cancelFinal.current();
  }, []);

  const onFrameScroll = useCallback(() => follow('frame'), [follow]);
  const onRailScroll = useCallback(() => follow('rail'), [follow]);

  return { frameRef, railRef, metrics, measure, onFrameScroll, onRailScroll };
}

export interface DualScrollProps {
  /** The frame's accessible name (it is a labelled region). */
  label: string;
  children: React.ReactNode;
  /** Extra class on the wrapper. */
  className?: string;
  /** Extra class on the scroll frame (the kit passes `board-scroll`). */
  frameClassName?: string;
  /** Lets a caller reach the frame (focus it, scroll a row into view). */
  frameRef?: React.MutableRefObject<HTMLDivElement | null>;
}

export default function DualScroll({ label, children, className, frameClassName, frameRef: outerRef }: DualScrollProps) {
  const { frameRef, railRef, metrics, onFrameScroll, onRailScroll } = useSyncedScroll<HTMLDivElement>();
  const setFrame = useCallback((el: HTMLDivElement | null) => {
    frameRef.current = el;
    if (outerRef) outerRef.current = el;
  }, [frameRef, outerRef]);
  return (
    <div className={['board-dual', className].filter(Boolean).join(' ')} data-overflow={metrics.overflow ? 'true' : 'false'}>
      {metrics.overflow && (
        <div
          ref={railRef}
          className="board-rail"
          data-rail="top"
          aria-hidden="true"
          tabIndex={-1}
          onScroll={onRailScroll}
          style={metrics.gutter ? { marginRight: metrics.gutter } : undefined}
        >
          <div className="board-rail__track" style={{ width: metrics.trackWidth }} />
        </div>
      )}
      <div
        ref={setFrame}
        className={['board-dual__frame', frameClassName].filter(Boolean).join(' ')}
        role="region"
        aria-label={label}
        tabIndex={0}
        onScroll={onFrameScroll}
      >
        {children}
      </div>
    </div>
  );
}
